import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Building2, Clock, ExternalLink, MapPin } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';
import { buildVendorDetailsUrl } from '../../config/s3Config';

import {
    fetchVerifyVendorQueue,
    fetchVerifyVendorDetail,
    approveVerifyVendor,
    clearVerifyVendorDetail,
    resetVerifyVendor,
    selectVVQueue, selectVVQueueLoading, selectVVQueueError,
    selectVVDetail, selectVVDetailLoading, selectVVDetailError,
    selectVVVerifyLoading,
} from '../../slices/purchaseSlice/verifyVendorSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

const SERVICE_PROVIDER = 'Service Provider';

// Service Provider compliance fields — legacy renders each one only when the value is non-null.
const SP_OPTIONAL_FIELDS = [
    ['VehicleEquipmentRCInsurance', 'Vehicle / Equipment RC & Insurance'],
    ['ESIRegistration', 'ESI Registration'],
    ['LabourLicense', 'Labour License'],
    ['ElectricalContractorLicense', 'Electrical Contractor License'],
    ['InsuranceWCLiability', 'Insurance (WC / Liability)'],
    ['PastExperienceProof', 'Past Experience Proof'],
    ['ManpowerListCertifications', 'Manpower List & Certifications'],
    ['ToolsEquipmentList', 'Tools & Equipment List'],
    ['QAQCProcedures', 'QA/QC Procedures'],
    ['HSEPolicySafetyPlan', 'HSE Policy / Safety Plan'],
    ['FinancialStatementsITR', 'Financial Statements / ITR'],
    ['BankStatementWCProof', 'Bank Statement / WC Proof'],
    ['PerformanceBGSecurity', 'Performance BG / Security'],
    ['ClientAlignedB2BDocs', 'Client-Aligned B2B Docs'],
    ['ApprovalValidity', 'Approval Validity'],
];

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Return: 'Returned for Update',
    Reject: 'Rejected Successfully',
};

const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const StatusBadge = ({ status }) => (status === '0' ? (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
) : (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
));

const VerifyVendor = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVVQueue);
    const queueLoading = useSelector(selectVVQueueLoading);
    const queueError = useSelector(selectVVQueueError);
    const detail = useSelector(selectVVDetail);
    const detailLoading = useSelector(selectVVDetailLoading);
    const detailError = useSelector(selectVVDetailError);
    const verifyLoading = useSelector(selectVVVerifyLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    // Only trust the detail that belongs to the row currently selected.
    const vendor = selectedItem && detail?.VendorCode === selectedItem.VendorCode ? detail : null;

    useEffect(() => {
        if (roleId) dispatch(fetchVerifyVendorQueue(roleId));
    }, [roleId, dispatch]);

    useEffect(() => {
        // Legacy's vendor status dropdown keeps "Return" (unlike most other verification pages).
        dispatch(setShowReturnButton('Yes'));
        return () => {
            dispatch(resetVerifyVendor());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedItem || !roleId) return;
        dispatch(clearVerifyVendorDetail());
        dispatch(fetchVerifyVendorDetail({ vendorCode: selectedItem.VendorCode, roleId }));
        setComment('');
        setShowRemarksHistory(false);
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // The queue row carries no MOID (only the detail SP returns it), so the status
    // dropdown and remarks history wait for the detail to land.
    useEffect(() => {
        if (!vendor?.MOID || !roleId) return;
        dispatch(fetchStatusList({ MOID: vendor.MOID, ROID: roleId, ChkAmt: 0 }));
        dispatch(setSelectedMOID(vendor.MOID));
        dispatch(fetchRemarks({ trno: vendor.VendorCode, moid: vendor.MOID }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendor?.MOID, vendor?.VendorCode]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchVerifyVendorQueue(roleId));
    };

    const handleItemSelect = (item) => {
        // Rows with Status === '0' were returned for update. Legacy opens an edit form
        // (/Purchase/EditVendor) for these instead of the verification view.
        if (item.Status === '0') {
            toast.info('This vendor was returned for update — there is nothing to verify until it is resubmitted.');
            return;
        }
        setSelectedItem(item);
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchVerifyVendorQueue(roleId));
            setSelectedItem(null);
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearVerifyVendorDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!vendor) { toast.error('No vendor selected'); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        // Legacy posts the dropdown option's Type text ("Approve" | "Verify" | "Return" | "Reject").
        const actionValue = action.type;
        const payload = {
            VendorCode: vendor.VendorCode,
            Action: actionValue,
            ApprovalNote: comment.trim(),
            VendorStatus: vendor.VendorStatus,
            VendorName: vendor.VendorName,
            RoleId: roleId,
            CreatedBy: getCurrentUser(),
        };

        try {
            const result = await dispatch(approveVerifyVendor(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            // spApproveVendor's own success literal is the single-t "Submited".
            if (status === 'Submited' || status === 'Submitted') {
                toast.success(`Vendor ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            // An empty status means the SP skipped every branch and saved nothing — e.g. Approve by a
            // role that isn't the configured VendorCreation approver.
            toast.error(status || `${actionValue} was not applied — the server returned no confirmation. If this was an Approve, check that your role is the configured Vendor Creation approver.`);
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Failed to submit vendor verification');
        }
    };

    const handleViewDocument = () => {
        const url = buildVendorDetailsUrl(vendor?.uploaddoc);
        if (!url) { toast.error('No uploaded document available'); return; }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return item.VendorName?.toLowerCase().includes(q)
            || item.VendorCode?.toLowerCase().includes(q)
            || item.Type?.toLowerCase().includes(q)
            || item.Address?.toLowerCase().includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.VendorName}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.VendorCode}</p>
                </div>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div className="flex items-center justify-between gap-2">
                    <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px]">{item.Type}</span>
                    <StatusBadge status={item.Status} />
                </div>
                {item.Address && (
                    <div className="flex items-start space-x-1"><MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" /><span className="line-clamp-2">{item.Address}</span></div>
                )}
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[180px] truncate">{item.VendorName}</span>
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{item.VendorCode}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[110px]">{item.Type}</span>
            <span className="text-gray-500 dark:text-gray-400 truncate max-w-[320px]">{item.Address}</span>
            <span className="ml-auto"><StatusBadge status={item.Status} /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderVendorDetails = () => {
        const isSP = vendor.Type === SERVICE_PROVIDER;
        const gstApplicable = vendor.GstApplicable === 'YES'; // SP returns 'YES' | 'No'
        const msme = vendor.MSMEApplicable === 'Yes';
        const ledger = vendor.Ledger;
        const spOptional = SP_OPTIONAL_FIELDS.filter(([key]) => vendor[key] != null);

        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Field label="Type" value={vendor.Type} />
                    <Field label="Vendor Code" value={vendor.VendorCode} />
                    <Field label="Vendor Name" value={vendor.VendorName} className="sm:col-span-2" />
                    <Field label="Phone No" value={vendor.Phoneno} />
                    <Field label="Mobile No" value={vendor.MobileNo} />
                    <Field label="Bank Name" value={vendor.BankName} />
                    <Field label="Account No" value={vendor.AccountNo} />
                    <Field label="IFSC Code" value={vendor.IFSCode} />
                    <Field label="Nature Group" value={vendor.NatureGroupName} />
                    <Field label="Master Group" value={vendor.MasterGroup} />
                    {vendor.SubGroupId !== 0 && <Field label="Sub Group" value={vendor.SubGroup} />}
                    <Field label="GST Applicable" value={vendor.GstApplicable} />
                    <Field label="MSME Applicable" value={vendor.MSMEApplicable} />
                    <Field label="Address" value={vendor.Address} className="sm:col-span-2" />
                    {vendor.uploaddoc && (
                        <div className="flex items-end">
                            <button
                                type="button"
                                onClick={handleViewDocument}
                                className="inline-flex items-center gap-1.5 text-sm font-bold text-red-800 dark:text-red-300 underline hover:no-underline"
                            >
                                <ExternalLink className="w-4 h-4" /> View Uploaded Document
                            </button>
                        </div>
                    )}
                </div>

                {msme && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Field label="Udyam Registration Number" value={vendor.UdyamRegistrationNumber} />
                        <Field label="Enterprise Type" value={vendor.Enterprisetype} />
                        <Field label="Major Activity" value={vendor.MajorActivity} />
                        <Field label="Classification Effective Date" value={vendor.ClassificationEffectiveDate} />
                        <Field label="Date of Commencement of Business / Production" value={vendor.DateofcommencementofBusinessorProduction} />
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {ledger && (
                        <>
                            <Field label="Opening Balance" value={ledger.Balance != null ? `₹ ${formatIndianCurrency(ledger.Balance)}` : null} />
                            <Field label="Balance As On Date" value={ledger.Date} />
                            <Field label="Ledger Value Type" value={ledger.ValueType} />
                        </>
                    )}
                    {isSP && <Field label="PF Reg No" value={vendor.PFRegNo} />}
                    <Field label="Email" value={vendor.EmailId} />
                    <Field label="PAN No" value={vendor.PanNo} />
                    {!isSP && <Field label="CST No" value={vendor.CstNo} />}
                    {isSP && <Field label="TDS Applicable" value={vendor.TDSApplicable} />}
                    {isSP && vendor.TDSApplicable === 'Yes' && <Field label="TDS Code" value={vendor.TDSCode} className="sm:col-span-2" />}
                </div>

                {isSP && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Field label="SP Category" value={vendor.SPCategory} />
                        <Field label="System Credential Flag" value={vendor.SystemCredentialFlag} />
                        <Field label="Risk Level" value={vendor.RiskLevel} />
                        <Field label="Owner ID / KYC" value={vendor.OwnerIDKYC} />
                        {spOptional.map(([key, label]) => <Field key={key} label={label} value={vendor[key]} />)}
                    </div>
                )}

                {gstApplicable && vendor.VendorGSTData?.length > 0 && (
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">GST Details</p>
                        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-100 dark:bg-gray-800">
                                        <th className="px-4 py-2 text-center text-[10px] font-bold text-gray-500 uppercase">State Name</th>
                                        <th className="px-4 py-2 text-center text-[10px] font-bold text-gray-500 uppercase">Tax No</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {vendor.VendorGSTData.map((g, i) => (
                                        <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                                            <td className="px-4 py-2 text-center text-gray-700 dark:text-gray-300">{g.StateName}</td>
                                            <td className="px-4 py-2 text-center text-gray-700 dark:text-gray-300">{g.TaxNo}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (detailLoading || (!vendor && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading vendor details…</span>
                </div>
            );
        }
        if (detailError || !vendor) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Vendor details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{vendor.VendorName}</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{vendor.VendorCode}</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">{vendor.Type}</span>
                        {vendor.VendorStatus && (
                            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">Vendor Status: {vendor.VendorStatus}</span>
                        )}
                    </div>
                </div>

                {renderVendorDetails()}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                <SimpleCommentInput
                    comment={comment}
                    onCommentChange={(e) => setComment(e.target.value)}
                    label="Notes"
                    placeholder="Enter verification notes…"
                    required
                    rows={3}
                    showCharCount
                />

                {statusLoading ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-center space-x-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                            <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                        </div>
                    </div>
                ) : statusError ? (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                        <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                    </div>
                ) : !hasActions || !enabledActions || enabledActions.length === 0 ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                        <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this vendor</p>
                    </div>
                ) : (
                    <ActionButtons
                        actions={enabledActions}
                        onActionClick={handleActionClick}
                        loading={verifyLoading}
                        isVerified={true}
                        comment={comment}
                        showValidation={true}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Vendor Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Building2}
                badgeText="Vendor Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by vendor name, code, type, address…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: handleItemSelect,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No vendors pending verification!',
                        itemKey: 'VendorId',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Vendor Verification',
                        icon: Building2,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Vendor Details',
                        icon: Building2,
                        selectedTitle: 'Vendor Verification',
                        emptyTitle: 'No Vendor Selected',
                        emptyMessage: 'Select a vendor from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyVendor;
